import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdOutlineReceiptLong, MdRefresh, MdPayment } from 'react-icons/md';
import { getMyBills } from '../../../api/services/billingService';
import { getRentalStatus } from '../../../api/services/rentalService';
import PayRentEvidenceModal from './PayRentEvidenceModal';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './MyBills.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatMoneyKes = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const hasCents = Math.floor(num) !== num;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `KES ${formatted}`;
};

const statusLabel = (value) => {
  const raw = String(value || '').toUpperCase();
  if (!raw) return 'Unknown';
  return raw.replace(/_/g, ' ');
};

const statusClass = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'paid') return 'tenant-bills-status--paid';
  if (normalized === 'pending') return 'tenant-bills-status--pending';
  if (normalized === 'overdue') return 'tenant-bills-status--overdue';
  return 'tenant-bills-status--other';
};

const MyBills = () => {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rental, setRental] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);

  useEffect(() => {
    let active = true;
    getRentalStatus()
      .then((res) => {
        if (active && res?.status === 'success') {
          setRental(res.data);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const loadBills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getMyBills(year, month);
      setBills(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setBills([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  };

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    const options = [];
    for (let y = current + 1; y >= current - 5; y -= 1) {
      options.push(y);
    }
    return options;
  }, [now]);

  const totals = useMemo(() => {
    return bills.reduce(
      (acc, row) => {
        acc.totalDue += Number(row.totalDue) || 0;
        acc.totalPaid += Number(row.totalPaid) || 0;
        acc.remainingDue += Number(row.remainingDue) || 0;
        return acc;
      },
      { totalDue: 0, totalPaid: 0, remainingDue: 0 }
    );
  }, [bills]);

  return (
    <div className="tenant-bills-page">
      <CollapsibleTopPanel id="tenant-bills-top" defaultOpenOnMobile={false}>
        <header className="tenant-bills-header">
          <div>
            <h1 className="tenant-bills-title">My bills</h1>
            <p className="tenant-bills-subtitle">Monthly rent and utility bills for your current tenancy.</p>
          </div>
          <button
            type="button"
            className="tenant-bills-refresh"
            onClick={loadBills}
            disabled={loading}
            aria-label="Refresh bills"
          >
            <MdRefresh className={loading ? 'spin' : ''} />
          </button>
        </header>

        <section className="tenant-bills-toolbar">
          <div className="tenant-bills-period-nav">
            <button type="button" className="tenant-bills-period-arrow" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <MdChevronLeft />
            </button>
            <div className="tenant-bills-period-selects">
              <select className="tenant-bills-period-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <select className="tenant-bills-period-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className="tenant-bills-period-arrow" onClick={() => shiftMonth(1)} aria-label="Next month">
              <MdChevronRight />
            </button>
          </div>
          <div className="tenant-bills-summary-card">
            <div className="tenant-bills-summary-label">{MONTH_NAMES[month - 1]} {year}</div>
            <div className="tenant-bills-summary-row">
              <span>Total due</span>
              <strong>{formatMoneyKes(totals.totalDue)}</strong>
            </div>
            <div className="tenant-bills-summary-row">
              <span>Paid</span>
              <strong>{formatMoneyKes(totals.totalPaid)}</strong>
            </div>
            <div className="tenant-bills-summary-row">
              <span>Balance</span>
              <strong>{formatMoneyKes(totals.remainingDue)}</strong>
            </div>
          </div>
        </section>
      </CollapsibleTopPanel>

      {loading && (
        <div className="tenant-bills-state">
          <div className="tenant-bills-skeleton tenant-bills-skeleton--wide" />
          <div className="tenant-bills-skeleton" />
        </div>
      )}

      {!loading && error && (
        <div className="tenant-bills-error" role="alert">
          <div className="tenant-bills-error-title">Could not load bills</div>
          <p className="tenant-bills-error-message">{error.message || 'Something went wrong while loading your bills.'}</p>
          <button type="button" className="tenant-bills-error-retry" onClick={loadBills}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && bills.length === 0 && (
        <div className="tenant-bills-empty">
          <div className="tenant-bills-empty-icon">
            <MdOutlineReceiptLong />
          </div>
          <h2 className="tenant-bills-empty-title">No bills for this period</h2>
          <p className="tenant-bills-empty-text">
            Bills for {MONTH_NAMES[month - 1]} {year} will appear here when generated.
          </p>
        </div>
      )}

      {!loading && !error && bills.length > 0 && (
        <div className="tenant-bills-list">
          {bills.map((bill) => (
            <article key={bill.id} className="tenant-bills-card">
              <div className="tenant-bills-card-head">
                <span className={`tenant-bills-status ${statusClass(bill.status)}`}>{statusLabel(bill.status)}</span>
                <span className="tenant-bills-period">
                  {MONTH_NAMES[(Number(bill.periodMonth) || month) - 1] || bill.periodMonth} {bill.periodYear || year}
                </span>
              </div>
              <div className="tenant-bills-main-amount">{formatMoneyKes(bill.totalDue)}</div>
              <div className="tenant-bills-grid">
                <div>
                  <span className="tenant-bills-label">Rent</span>
                  <span className="tenant-bills-value">{formatMoneyKes(bill.rentAmount)}</span>
                </div>
                <div>
                  <span className="tenant-bills-label">Water</span>
                  <span className="tenant-bills-value">{formatMoneyKes(bill.waterAmount)}</span>
                </div>
                <div>
                  <span className="tenant-bills-label">Water units</span>
                  <span className="tenant-bills-value">
                    {Number.isFinite(Number(bill.waterUnits)) ? Number(bill.waterUnits).toLocaleString() : '—'}
                  </span>
                </div>
                <div>
                  <span className="tenant-bills-label">Penalty</span>
                  <span className="tenant-bills-value">{formatMoneyKes(bill.penaltyAmount)}</span>
                </div>
                <div>
                  <span className="tenant-bills-label">Credit applied</span>
                  <span className="tenant-bills-value">{formatMoneyKes(bill.creditApplied)}</span>
                </div>
                <div>
                  <span className="tenant-bills-label">Total paid</span>
                  <span className="tenant-bills-value">{formatMoneyKes(bill.totalPaid)}</span>
                </div>
                <div>
                  <span className="tenant-bills-label">Remaining due</span>
                  <span className="tenant-bills-value tenant-bills-value--danger">{formatMoneyKes(bill.remainingDue)}</span>
                </div>
              </div>
              {Number(bill.remainingDue) > 0 && (
                <div className="tenant-bills-card-actions">
                  <button
                    type="button"
                    className="tenant-bills-pay-btn"
                    onClick={() => setPayModalOpen(true)}
                  >
                    <MdPayment className="btn-icon" />
                    <span>Submit Payment Evidence</span>
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <PayRentEvidenceModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        rental={rental}
        onSubmitted={() => {
          loadBills();
        }}
      />
    </div>
  );
};

export default MyBills;
